import { db } from '../database'
import { ref, unref, watch, isRef, Ref, ComputedRef } from 'vue'
import { deepUnwrap, createUpdateSet } from '../util'
import dayjs from 'dayjs'
import { CacheStateRepository, CacheStateTable } from '../cacheState.store'
import {
  BrowserDeployment,
  BrowserDeploymentPostIn,
  BrowserDeploymentUpdateIn,
  BrowserExtensionManagementService,
  BrowserLastUsage,
  UsageStats,
  UsageStatsDetails,
} from '@/client'
import {
  applyBrowserLastUsageFilters,
  applyBrowserLastUsageBySoftwareFilters,
  BrowserLastUsageFilters,
  BrowserLastUsageBySoftwareFilters,
} from './browserextension.filter'
import {
  createBrowserDeploymentTable,
  createBrowserLastUsageTable,
  deserializeBrowserDeployment,
  deserializeBrowserLastUsage,
  serializeBrowserDeployment,
  serializeBrowserLastUsage,
} from './browserextension.models'
import {
  _getLastUsages,
  _getDeployments,
  _deleteDeployment,
  _createDeployment,
  _patchDeployment,
} from './browserextension.requests'
import { sql } from 'kysely'
import { useI18n } from 'vue-i18n'

// =============================================================================
// TYPES
// =============================================================================

export type Pagination = {
  limit?: number
  offset?: number
}

export type SortLastUsage = {
  column?: keyof BrowserLastUsage
  direction?: 'asc' | 'desc'
}

export type SortDeployment = {
  column?: keyof BrowserDeployment
  direction?: 'asc' | 'desc'
}

export type EnrichedBrowserLastUsage = BrowserLastUsage & {
  deployment_name: string
  deployment_description: string
}

export type BrowserLastUsageBySoftware = {
  _id: string
  software_name: string
  deployment_id: string | null
  deployment_name: string | undefined
  count?: string | number | bigint
  latestUsage?: string
  active_users?: string | number | bigint
  total_deployment_users?: string | number | bigint
}

export interface DeploymentOption {
  label: string
  value: string
}

/** Symbol for provide/inject */
export const PERSON_STATS_KEY = Symbol('personStats')

export interface PersonStatsContext {
  personStats: Ref<UsageStatsDetails | null>
  loading: Ref<boolean>
  error: Ref<Error | null>
  refetch: () => Promise<void>
}

// =============================================================================
// REACTIVE STATE
// =============================================================================

export const lastCacheChange = ref<Date>()
export const loadingFullReload = ref<boolean>(false)

// =============================================================================
// BROWSER LAST USAGE - DATABASE OPERATIONS
// =============================================================================

async function bulkInsertBrowserLastUsages(
  lastUsages: BrowserLastUsage[],
  batchSize: number = 1000
) {
  const serializedLastUsages = lastUsages.map(serializeBrowserLastUsage)

  await db.transaction().execute(async (trx) => {
    for (let i = 0; i < serializedLastUsages.length; i += batchSize) {
      const batch = serializedLastUsages.slice(i, i + batchSize)
      await trx
        .insertInto('browser_last_usage')
        .values(batch)
        .onConflict((oc) =>
          oc.column('_id').doUpdateSet(createUpdateSet(serializedLastUsages[0]))
        )
        .execute()
    }
  })
  if (lastUsages.length > 0) {
    lastCacheChange.value = new Date()
  }
  return lastUsages
}

async function refreshBrowserLastUsageCacheFromApi(
  filter: BrowserLastUsageFilters = {},
  includeDeleted: boolean = false
) {
  const results = await _getLastUsages({
    updatedGte: filter.updatedAfter,
    updatedLt: filter.updatedBefore,
    deletedAtGte: filter.deletedAfter,
    includeDeleted: includeDeleted,
  })

  if (!results.error && results.data) {
    await bulkInsertBrowserLastUsages(results.data)
  }
  return results
}

async function getBrowserLastUsage(
  filters: BrowserLastUsageFilters = {},
  pagination: Pagination = {},
  sort: SortLastUsage = {},
  join: boolean = false
): Promise<BrowserLastUsage[] | EnrichedBrowserLastUsage[]> {
  let query = db.selectFrom('browser_last_usage').selectAll()

  query = applyBrowserLastUsageFilters(query, filters)

  if (join) {
    query = query
      .leftJoin(
        'browser_deployment',
        'browser_deployment._id',
        'browser_last_usage.deployment_id'
      )
      .select([
        'browser_deployment.name as deployment_name',
        'browser_deployment.description as deployment_description',
      ])
  }

  if (pagination.limit) {
    query = query.limit(pagination.limit)
  }
  if (pagination.offset) {
    query = query.offset(pagination.offset)
  }
  if (sort.column) {
    query = query.orderBy(sort.column, sort.direction ?? 'asc')
  }

  const results = await query.execute()
  return results.map(deserializeBrowserLastUsage)
}

async function getBrowserLastUsages(
  installationId?: string | null,
  fromDate?: Date | null,
  tillDate?: Date | null,
  limit?: number | null
): Promise<BrowserLastUsage[]> {
  const results =
    await BrowserExtensionManagementService.getBrowserLastUsagesApiV1BrowserLastUsagesGet(
      {
        installationId: installationId || null,
        createdGte: fromDate ? fromDate.toISOString() : null,
        createdLte: tillDate ? tillDate.toISOString() : null,
        limit: limit || null,
        sort: ['created:desc'],
      }
    )

  if (Array.isArray(results)) {
    return results
  }

  return []
}

// =============================================================================
// BROWSER DEPLOYMENTS - DATABASE OPERATIONS
// =============================================================================

async function bulkInsertBrowserDeployments(
  deployments: BrowserDeployment[],
  batchSize: number = 1000
) {
  const serializedDeployments = deployments.map(serializeBrowserDeployment)

  await db.transaction().execute(async (trx) => {
    for (let i = 0; i < serializedDeployments.length; i += batchSize) {
      const batch = serializedDeployments.slice(i, i + batchSize)
      await trx
        .insertInto('browser_deployment')
        .values(batch)
        .onConflict((oc) =>
          oc
            .column('_id')
            .doUpdateSet(createUpdateSet(serializedDeployments[0]))
        )
        .execute()
    }
  })
  if (deployments.length > 0) {
    lastCacheChange.value = new Date()
  }
  return deployments
}

async function refreshBrowserDeploymentCacheFromApi() {
  const results = await _getDeployments({})

  if (!results.error && results.data) {
    await bulkInsertBrowserDeployments(results.data as BrowserDeployment[])
  }
  return results
}

async function getBrowserDeployments(
  pagination: Pagination = {},
  sort: SortDeployment = {}
): Promise<BrowserDeployment[]> {
  let query = db.selectFrom('browser_deployment').selectAll()

  if (pagination.limit) {
    query = query.limit(pagination.limit)
  }
  if (pagination.offset) {
    query = query.offset(pagination.offset)
  }
  if (sort.column) {
    query = query.orderBy(sort.column, sort.direction ?? 'asc')
  }

  const results = await query.execute()
  return results.map(deserializeBrowserDeployment)
}

async function createDeploymentStore(newDeployment: BrowserDeploymentPostIn) {
  return _createDeployment(newDeployment).then(async (response) => {
    if (!response.error && response.data) {
      const serializedBrowserDeployment = serializeBrowserDeployment(
        response.data
      )
      await db
        .insertInto('browser_deployment')
        .values(serializedBrowserDeployment)
        .execute()
    }
    lastCacheChange.value = new Date()
    return response
  })
}

async function updateDeploymentStore(
  id: string,
  updates: BrowserDeploymentUpdateIn
) {
  return await _patchDeployment(id, updates).then(async (response) => {
    if (response.error) return response

    const serializedUpdates = serializeBrowserDeployment({
      ...response.data,
    } as BrowserDeployment)

    await db
      .updateTable('browser_deployment')
      .set(serializedUpdates)
      .where('browser_deployment._id', '=', id)
      .execute()

    lastCacheChange.value = new Date()
    return response
  })
}

async function deleteDeploymentStore(id: string) {
  return _deleteDeployment(id).then(async (response) => {
    await db
      .deleteFrom('browser_deployment')
      .where('browser_deployment._id', '=', id)
      .execute()
    lastCacheChange.value = new Date()
    return response
  })
}

// =============================================================================
// STATISTICS - API OPERATIONS
// =============================================================================

async function getToolUsageStats(
  installationId: string | null,
  softwareName: string,
  fromDate?: Date | null,
  tillDate?: Date | null,
  deploymentId?: string | null
): Promise<UsageStatsDetails | null> {
  const results =
    await BrowserExtensionManagementService.getToolUsageStatsApiV1BrowserStatisticsToolsSoftwareNameGet(
      {
        installationId,
        deploymentId,
        softwareName,
        fromDate: fromDate ? fromDate.toISOString() : null,
        tillDate: tillDate ? tillDate.toISOString() : null,
      }
    )

  return results || null
}

async function getToolUsageStatsByDeployment(
  deploymentId: string | null,
  softwareName: string,
  fromDate?: Date | null,
  tillDate?: Date | null
): Promise<UsageStatsDetails | null> {
  return getToolUsageStats(null, softwareName, fromDate, tillDate, deploymentId)
}

async function getToolsStatsByDeployment(
  deploymentId: string | null,
  fromDate?: Date | null,
  tillDate?: Date | null
): Promise<UsageStats[]> {
  const results =
    await BrowserExtensionManagementService.getAllToolsStatsApiV1BrowserStatisticsToolsGet(
      {
        deploymentId: deploymentId || null,
        fromDate: fromDate ? fromDate.toISOString() : null,
        tillDate: tillDate ? tillDate.toISOString() : null,
      }
    )

  if (Array.isArray(results)) {
    return results
  }
  return []
}

async function getAllInstallationsStats(
  fromDate?: Date | null,
  tillDate?: Date | null,
  deploymentId?: string | null
): Promise<UsageStats[]> {
  const params: {
    fromDate: string | null
    tillDate: string | null
    deploymentId?: string
  } = {
    fromDate: fromDate ? fromDate.toISOString() : null,
    tillDate: tillDate ? tillDate.toISOString() : null,
  }
  if (deploymentId) {
    params.deploymentId = deploymentId
  }

  const results =
    await BrowserExtensionManagementService.getAllInstallationsStatsApiV1BrowserStatisticsInstallationsGet(
      params
    )

  if (Array.isArray(results)) {
    return results
  }

  return []
}

async function getDeploymentUsageStats(
  deploymentId: string,
  fromDate?: Date | null,
  tillDate?: Date | null
): Promise<UsageStatsDetails | null> {
  const results =
    await BrowserExtensionManagementService.getDeploymentUsageStatsApiV1BrowserStatisticsDeploymentsDeploymentIdGet(
      {
        deploymentId,
        fromDate: fromDate ? fromDate.toISOString() : null,
        tillDate: tillDate ? tillDate.toISOString() : null,
      }
    )

  return results || null
}

async function getPersonUsageStats(
  installationId: string,
  fromDate?: Date | null,
  tillDate?: Date | null
): Promise<UsageStatsDetails | null> {
  const results =
    await BrowserExtensionManagementService.getInstallationUsageStatsApiV1BrowserStatisticsInstallationsInstallationIdGet(
      {
        installationId,
        fromDate: fromDate ? fromDate.toISOString() : null,
        tillDate: tillDate ? tillDate.toISOString() : null,
      }
    )

  return results || null
}

// =============================================================================
// LAST USAGE BY SOFTWARE - AGGREGATED QUERY
// =============================================================================

async function getLastUsageBySoftware(
  filters: BrowserLastUsageBySoftwareFilters
) {
  let query = db.selectFrom('browser_last_usage').selectAll()

  query = applyBrowserLastUsageBySoftwareFilters(query, filters)

  // Subquery to get total unique users per deployment
  const totalUsersPerDeployment = db
    .selectFrom('browser_last_usage')
    .select([
      'deployment_id',
      db.fn.count<string>('installation_id').distinct().as('total_users'),
    ])
    .groupBy('deployment_id')
    .as('deployment_stats')

  query = query
    .leftJoin(
      'browser_deployment',
      'browser_deployment._id',
      'browser_last_usage.deployment_id'
    )
    .leftJoin(
      totalUsersPerDeployment,
      'deployment_stats.deployment_id',
      'browser_last_usage.deployment_id'
    )
    .where('browser_deployment.deleted', '=', 0)
    .select([
      sql`hex(randomblob(8))`.as('_id'),
      'browser_last_usage.software_name',
      'browser_last_usage.deployment_id',
      db.fn.count('browser_last_usage.software_name').as('count'),
      db.fn
        .count<string>('browser_last_usage.installation_id')
        .distinct()
        .as('active_users'),
      'deployment_stats.total_users as total_deployment_users',
      db.fn.max('browser_last_usage.timestamp').as('latestUsage'),
      'browser_deployment.name as deployment_name',
      'browser_deployment.deleted_at as deployment_deleted_at',
    ])
    .groupBy([
      'browser_last_usage.software_name',
      'browser_last_usage.deployment_id',
      'browser_deployment.name',
      'deployment_stats.total_users',
    ])

  const results = await query.execute()
  return results
}

// =============================================================================
// COMPOSABLES - USE FUNCTIONS
// =============================================================================

function useBrowserLastUsage(
  initialFilters:
    | BrowserLastUsageFilters
    | Ref<BrowserLastUsageFilters>
    | ComputedRef<BrowserLastUsageFilters> = {},
  initialPagination:
    | Pagination
    | Ref<Pagination>
    | ComputedRef<Pagination> = {},
  initialSort:
    | SortLastUsage
    | Ref<SortLastUsage>
    | ComputedRef<SortLastUsage> = {},
  join: boolean = false
) {
  const data = join
    ? ref<EnrichedBrowserLastUsage[]>([])
    : ref<BrowserLastUsage[]>([])

  const fetchData = async () => {
    const filters = deepUnwrap(initialFilters) as BrowserLastUsageFilters
    const pagination = deepUnwrap(initialPagination) as Pagination
    const sort = deepUnwrap(initialSort) as SortLastUsage
    data.value = await getBrowserLastUsage(filters, pagination, sort, join)
  }

  const stopWatch = watch(
    [
      () => unref(initialFilters),
      () => unref(initialPagination),
      () => unref(initialSort),
      lastCacheChange,
    ],
    async () => {
      try {
        await fetchData()
      } catch (e) {
        handleApiError(e, 'fetch browser last usage')
      }
    },
    { deep: true }
  )

  const updateParameters = (
    newFilters: Partial<BrowserLastUsageFilters> = {},
    newPagination: Partial<Pagination> = {},
    newSort: Partial<SortLastUsage> = {}
  ) => {
    initialFilters = mergeUpdates(initialFilters, newFilters)
    initialPagination = mergeUpdates(initialPagination, newPagination)
    initialSort = mergeUpdates(initialSort, newSort)

    fetchData()
  }

  fetchData().catch((e) =>
    handleApiError(e, 'initial fetch browser last usage')
  )

  return {
    data,
    unsubscribe: stopWatch,
    updateParameters,
    lastCacheUpdate: lastCacheChange,
  }
}

function useBrowserLastUsages(
  installationId: Ref<string> | ComputedRef<string> | (string | null),
  initialFromDate: Date | Ref<Date> | ComputedRef<Date> | null = null,
  initialTillDate: Date | Ref<Date> | ComputedRef<Date> | null = null
) {
  const data = ref<BrowserLastUsage[]>([])
  const loading = ref<boolean>(false)
  const error = ref<Error | null>(null)

  const fetchData = async () => {
    loading.value = true
    error.value = null
    try {
      const id = unref(installationId) as string | null
      const fromDate = unref(initialFromDate) as Date | null
      const tillDate = unref(initialTillDate) as Date | null
      const result = await getBrowserLastUsages(id, fromDate, tillDate, null)
      data.value = result
    } catch (e) {
      error.value = e as Error
      handleApiError(e, 'fetch browser last usages')
    } finally {
      loading.value = false
    }
  }

  const stopWatch = watch(
    [
      () => unref(installationId),
      () => unref(initialFromDate),
      () => unref(initialTillDate),
    ],
    async () => {
      try {
        await fetchData()
      } catch (e) {
        handleApiError(e, 'fetch browser last usages')
      }
    },
    { deep: true }
  )

  const refetch = () => fetchData()

  fetchData().catch((e) =>
    handleApiError(e, 'initial fetch browser last usages')
  )

  return {
    data,
    loading,
    error,
    unsubscribe: stopWatch,
    refetch,
  }
}

function useBrowserDeployments(
  initialPagination:
    | Pagination
    | Ref<Pagination>
    | ComputedRef<Pagination> = {},
  initialSort:
    | SortDeployment
    | Ref<SortDeployment>
    | ComputedRef<SortDeployment> = {}
) {
  const data = ref<BrowserDeployment[]>([])

  const fetchData = async () => {
    const pagination = deepUnwrap(initialPagination) as Pagination
    const sort = deepUnwrap(initialSort) as SortDeployment
    data.value = await getBrowserDeployments(pagination, sort)
  }

  const stopWatch = watch(
    [() => unref(initialPagination), () => unref(initialSort), lastCacheChange],
    async () => {
      try {
        await fetchData()
      } catch (e) {
        handleApiError(e, 'fetch browser deployments')
      }
    },
    { deep: true }
  )

  const updateParameters = (
    newPagination: Partial<Pagination> = {},
    newSort: Partial<SortDeployment> = {}
  ) => {
    initialPagination = mergeUpdates(initialPagination, newPagination)
    initialSort = mergeUpdates(initialSort, newSort)

    fetchData()
  }

  fetchData().catch((e) =>
    handleApiError(e, 'initial fetch browser deployments')
  )

  return {
    data,
    unsubscribe: stopWatch,
    updateParameters,
    lastCacheUpdate: lastCacheChange,
  }
}

function useBrowserLastUsageBySoftware(
  initialFilters:
    | BrowserLastUsageBySoftwareFilters
    | Ref<BrowserLastUsageBySoftwareFilters>
    | ComputedRef<BrowserLastUsageBySoftwareFilters> = {}
) {
  const data = ref<BrowserLastUsageBySoftware[]>([])

  const fetchData = async () => {
    const filters = deepUnwrap(initialFilters) as BrowserLastUsageFilters

    data.value = await getLastUsageBySoftware(filters)
  }

  const stopWatch = watch(
    [() => unref(initialFilters), lastCacheChange],
    async () => {
      await fetchData()
    },
    { deep: true }
  )

  fetchData()

  return {
    data,
    unsubscribe: stopWatch,
    lastCacheUpdate: lastCacheChange,
  }
}

function useInstallationsStats(
  initialFromDate:
    | Date
    | Ref<Date | null>
    | ComputedRef<Date | null>
    | null = null,
  initialTillDate:
    | Date
    | Ref<Date | null>
    | ComputedRef<Date | null>
    | null = null,
  initialDeploymentId:
    | string
    | Ref<string | null>
    | ComputedRef<string | null>
    | null = null
) {
  const data = ref<UsageStats[]>([])
  const loading = ref<boolean>(false)
  const error = ref<Error | null>(null)

  const fetchData = async () => {
    loading.value = true
    error.value = null
    try {
      const fromDate = unref(initialFromDate) as Date | null
      const tillDate = unref(initialTillDate) as Date | null
      const deploymentId = unref(initialDeploymentId) as string | null

      const result = await getAllInstallationsStats(
        fromDate,
        tillDate,
        deploymentId
      )
      data.value = result
    } catch (e) {
      error.value = e as Error
      handleApiError(e, 'fetch installations stats')
    } finally {
      loading.value = false
    }
  }

  const stopWatch = watch(
    [
      () => unref(initialFromDate),
      () => unref(initialTillDate),
      () => unref(initialDeploymentId),
    ],
    async () => {
      try {
        await fetchData()
      } catch (e) {
        handleApiError(e, 'fetch installations stats')
      }
    },
    { deep: true }
  )

  const updateParameters = (
    newFromDate?: Date | null,
    newTillDate?: Date | null,
    newDeploymentId?: string | null
  ) => {
    if (newFromDate !== undefined) initialFromDate = newFromDate
    if (newTillDate !== undefined) initialTillDate = newTillDate
    if (newDeploymentId !== undefined) initialDeploymentId = newDeploymentId
    fetchData()
  }

  const refetch = () => fetchData()

  fetchData().catch((e) =>
    handleApiError(e, 'initial fetch installations stats')
  )

  return {
    data,
    loading,
    error,
    unsubscribe: stopWatch,
    updateParameters,
    refetch,
  }
}

function useDeploymentOptions() {
  const i18n = useI18n()
  const { data: deployments, unsubscribe } = useBrowserDeployments()

  const deploymentOptions = computed<DeploymentOption[]>(() => {
    const options: DeploymentOption[] = [
      {
        label: i18n.t('browserExtension.allDeployments'),
        value: 'all',
      },
    ]

    if (deployments.value) {
      const deploymentOpts = deployments.value.map((d) => ({
        label: d.name,
        value: d._id,
      }))
      options.push(...deploymentOpts)
    }

    return options
  })

  const selectedDeploymentOption = ref<DeploymentOption[] | undefined>(
    undefined
  )

  watch(
    deploymentOptions,
    (options) => {
      if (!selectedDeploymentOption.value && options.length > 0) {
        selectedDeploymentOption.value = [options[0]]
      }
    },
    { immediate: true }
  )

  const selectedDeploymentId = computed<string | null>(() => {
    const value = selectedDeploymentOption.value?.[0]?.value
    return value === 'all' ? null : value || null
  })

  return {
    deploymentOptions,
    selectedDeploymentOption,
    selectedDeploymentId,
    deployments,
    unsubscribe,
  }
}

function usePersonStats(
  installationId:
    | Ref<string | null>
    | ComputedRef<string | null>
    | string
    | null,
  fromDate?: Ref<Date | null> | ComputedRef<Date | null> | Date | null,
  tillDate?: Ref<Date | null> | ComputedRef<Date | null> | Date | null
): PersonStatsContext {
  const personStats = ref<UsageStatsDetails | null>(null)
  const loading = ref<boolean>(false)
  const error = ref<Error | null>(null)

  let currentRequestId = 0

  const fetchStats = async () => {
    const requestId = ++currentRequestId

    const id = unref(installationId)

    if (!id) {
      personStats.value = null
      return
    }

    loading.value = true
    error.value = null

    try {
      const from = unref(fromDate) ?? null
      const till = unref(tillDate) ?? null

      const result = await getPersonUsageStats(id, from, till)

      if (requestId === currentRequestId) {
        personStats.value = result
      }
    } catch (e) {
      if (requestId === currentRequestId) {
        error.value = e as Error
        console.error('Failed to fetch person stats:', getErrorMessage(e))
      }
    } finally {
      if (requestId === currentRequestId) {
        loading.value = false
      }
    }
  }

  const refetch = async () => {
    await fetchStats()
  }

  watch(
    [() => unref(installationId), () => unref(fromDate), () => unref(tillDate)],
    () => {
      fetchStats()
    },
    { immediate: true }
  )

  return {
    personStats,
    loading,
    error,
    refetch,
  }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

function mergeUpdates<T extends object>(
  source: T | Ref<T>,
  updates: Partial<T>
): T {
  if (isRef(source)) {
    return { ...source.value, ...updates }
  }
  return { ...source, ...updates }
}

function handleApiError(error: unknown, context: string): void {
  const message = error instanceof Error ? error.message : String(error)
  console.error(`[BrowserExtension] ${context}:`, message)
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  return String(error)
}

// =============================================================================
// MAIN STORE EXPORT
// =============================================================================

/**
 * BrowserExtensionStore handles the orchestration of browser extension data.
 * It provides methods to update caches and manage the local database.
 *
 * It acts as a facade for common orchestration tasks.
 * For specific data operations, use the exported functions directly.
 */
export const BrowserExtensionStore = {
  // Reactive state
  loadingFullReload,
  lastCacheChange,

  // Cache update methods
  async updateBrowserDeploymentCacheFromApi() {
    try {
      await refreshBrowserDeploymentCacheFromApi()
    } catch (error) {
      console.error('Failed to update browser deployment cache:', error)
      throw error
    }
  },

  async updateBrowserLastUsageCacheFromApi(fullReload: boolean = false) {
    let isFullReload = true

    try {
      const lastRefresh =
        await CacheStateRepository.getCacheStateById('browser_last_usage')

      const filter: BrowserLastUsageFilters = {}
      if (lastRefresh && lastRefresh.lastUpdate && !fullReload) {
        filter.updatedAfter = lastRefresh.lastUpdate
        isFullReload = false
      }

      loadingFullReload.value = isFullReload

      await refreshBrowserLastUsageCacheFromApi(filter)

      if (lastRefresh && lastRefresh.lastUpdate && !fullReload) {
        await refreshBrowserLastUsageCacheFromApi(
          {
            deletedAfter: lastRefresh?.lastUpdate,
          },
          true // includeDeleted
        )
      }

      type UpdateCacheStateTable = Partial<Omit<CacheStateTable, 'id'>> & {
        id: string
      }
      const update: UpdateCacheStateTable = {
        id: 'browser_last_usage',
        lastUpdate: dayjs().toISOString(),
      }

      if (isFullReload) {
        update.lastCompleteLoad = dayjs().toISOString()
      }

      await CacheStateRepository.upsertCacheState(update)
    } catch (error) {
      console.error('Failed to update browser last usage cache:', error)
      throw error
    } finally {
      loadingFullReload.value = false
    }
  },

  async reset() {
    await db.schema.dropTable('browser_deployment').ifExists().execute()
    await db.schema.dropTable('browser_last_usage').ifExists().execute()

    await createBrowserDeploymentTable()
    await createBrowserLastUsageTable()

    lastCacheChange.value = undefined
  },

  getDatabase() {
    return db.selectFrom('browser_last_usage')
  },
}

// =============================================================================
// NAMED EXPORTS FOR DIRECT USAGE
// =============================================================================

// Last Usage exports
export {
  bulkInsertBrowserLastUsages,
  getBrowserLastUsage,
  getBrowserLastUsages,
  refreshBrowserLastUsageCacheFromApi,
}
export { useBrowserLastUsage, useBrowserLastUsages }

// Deployment exports
export {
  bulkInsertBrowserDeployments,
  getBrowserDeployments,
  refreshBrowserDeploymentCacheFromApi,
}
export {
  createDeploymentStore as createDeployment,
  updateDeploymentStore as updateDeployment,
  deleteDeploymentStore as deleteDeployment,
}
export { useBrowserDeployments }

// Statistics exports
export {
  getToolUsageStats,
  getToolUsageStatsByDeployment,
  getToolsStatsByDeployment,
}
export {
  getAllInstallationsStats,
  getPersonUsageStats,
  getDeploymentUsageStats,
}
export { useInstallationsStats }

// Last Usage by Software exports
export { getLastUsageBySoftware, useBrowserLastUsageBySoftware }

// Composable exports
export { useDeploymentOptions, usePersonStats }
